<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-2">
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
          <h2 class="text-h5 font-weight-bold mb-0">Transaction Report</h2>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card outlined class="pa-4 mb-4">
      <v-row align="center">
        <!-- Branch -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.branch_id"
            :items="branches"
            item-text="name"
            item-value="id"
            label="Branch"
            dense
            outlined
            clearable
          />
        </v-col>

        <!-- Biller -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.biller_id"
            :items="billers"
            item-text="name"
            item-value="id"
            label="Biller"
            dense
            outlined
            clearable
          />
        </v-col>

        <!-- From Date -->
        <v-col cols="12" sm="6" md="2">
          <v-menu
            v-model="fromDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.start_date"
                label="From Date"
                readonly
                dense
                outlined
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="filters.start_date"
              @input="fromDateMenu = false"
            />
          </v-menu>
        </v-col>

        <!-- To Date -->
        <v-col cols="12" sm="6" md="2">
          <v-menu
            v-model="toDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.end_date"
                label="To Date"
                readonly
                dense
                outlined
                append-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="filters.end_date"
              @input="toDateMenu = false"
            />
          </v-menu>
        </v-col>

        <!-- Search Button -->
        <v-col cols="12" sm="6" md="1">
          <v-btn
            color="success"
            block
            height="40"
            :loading="loading"
            @click="generateReport"
          >
            <v-icon small>mdi-magnify</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Loading -->
    <v-row v-if="loading" justify="center">
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-row>

    <!-- Error -->
    <v-alert
      v-if="error"
      type="error"
      dense
      outlined
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <!-- Report -->
    <v-card outlined v-if="reportLoaded">
      <!-- Sales Transactions -->
      <v-simple-table dense class="transaction-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th width="180">Invoice No</th>
              <th>Customer</th>
              <th>Product</th>
              <th width="180">Date</th>
              <th width="120" class="text-right">Amount</th>
            </tr>
          </thead>

          <tbody>
            <!-- Sales -->
            <template
              v-for="sale in report.details.sales"
            >
              <tr
                v-for="(item, index) in sale.items"
                :key="`${sale.invoice_number}-${index}`"
              >
                <td>{{ sale.invoice_number }}</td>

                <td>
                  {{ sale.customer_name }}
                </td>

                <td>
                  {{ item.product_name }}
                  <small class="grey--text">
                    ({{ item.quantity }} x {{ item.selling_price }})
                  </small>
                </td>

                <td>
                  {{ formatDate(sale.invoice_date) }}
                </td>

                <td class="text-right">
                  {{ formatMoney(item.total_amount) }}
                </td>
              </tr>
            </template>

            <!-- Expenses Header -->
            <tr class="summary-header">
              <td colspan="5">
                <strong>Total Expenses</strong>
              </td>
            </tr>

            <!-- Expenses -->
            <tr
              v-for="(expense, index) in report.details.expenses"
              :key="`expense-${index}`"
              class="expense-row"
            >
              <td>
                {{ expense.debit_gl_account }}
              </td>

              <td>
                {{ expense.payee }}
              </td>

              <td>
                {{ expense.debit_gl_sub_header }}
              </td>

              <td>
                {{ formatDate(expense.date) }}
              </td>

              <td class="text-right">
                {{ formatMoney(expense.amount) }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Summary -->
      <v-divider />

      <v-card-text>
        <v-row justify="end">
          <v-col cols="12" md="4">
            <table width="100%">
              <tbody>
                <tr>
                  <td><strong>Total Sales:</strong></td>
                  <td class="text-right">
                    {{ formatMoney(report.summary.total_sales) }}
                  </td>
                </tr>

                <tr>
                  <td><strong>Total Payments:</strong></td>
                  <td class="text-right">
                    {{ formatMoney(report.summary.total_payments) }}
                  </td>
                </tr>

                <tr>
                  <td><strong>Total Expenses:</strong></td>
                  <td class="text-right">
                    {{ formatMoney(report.summary.total_expenses) }}
                  </td>
                </tr>

                <tr>
                  <td><strong>Bank Deposits:</strong></td>
                  <td class="text-right">
                    {{ formatMoney(report.summary.bank_deposits) }}
                  </td>
                </tr>

                <tr class="final-balance-row">
                  <td><strong>Final Balance:</strong></td>
                  <td class="text-right">
                    <strong>
                      {{ formatMoney(report.summary.final_balance) }}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Empty -->
    <v-alert
      v-if="!loading && reportLoaded && !report.details.sales.length"
      type="info"
      outlined
      dense
      class="mt-4"
    >
      No transactions found
    </v-alert>
  </v-container>
</template>

<script>
import { ReportService } from '@/services/report-service';
import { InvoiceService } from "@/services/invoice-service.js";

export default {
  data() {
    return {
      loading: false,
      error: null,

      fromDateMenu: false,
      toDateMenu: false,

      reportLoaded: false,

      report: {
        summary: {
          total_sales: 0,
          total_payments: 0,
          total_expenses: 0,
          bank_deposits: 0,
          final_balance: 0,
        },
        details: {
          sales: [],
          expenses: [],
        },
      },

      filters: {
        branch_id: null,
        biller_id: null,
        start_date: null,
        end_date: null,
      },

      branches: [],
      billers: [],
    };
  },
  mounted() {
    this.fetchBranches();
  },
  methods: {
      async fetchBranches() {
      try {
        const res = await InvoiceService.getBranches();
        this.branches = res.data || [];
      } catch (e) {
        this.branches = [];
      }
    },
    async generateReport() {
      this.loading = true;
      this.error = null;

      try {
        const response =
          await ReportService.getTransactionReport(
            this.filters
          );

        this.report = response.data.data;

        this.reportLoaded = true;
      } catch (err) {
        this.error =
          err.response?.data?.message ||
          'Failed to load transaction report';
      } finally {
        this.loading = false;
      }
    },

    formatMoney(value) {
      return Number(value || 0).toLocaleString(
        undefined,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      );
    },

    formatDate(date) {
      if (!date) return '-';

      return new Date(date).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.transaction-table {
  font-size: 13px;
}

.transaction-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #444;
}

.transaction-table td,
.transaction-table th {
  padding: 8px !important;
  border-bottom: 1px solid #e0e0e0;
}

.summary-header {
  background: #e8f5e9;
}

.expense-row {
  background: #fffdf2;
}

.final-balance-row td {
  padding-top: 12px;
  border-top: 2px solid #ccc;
}
</style>
